import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { login } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';
import { encrypt } from '../../utils/crypto';
import { rememberReturnTo, returnStateFrom } from '../../utils/authRedirect';
import SocialAuthButtons from '../../components/Auth/SocialAuthButtons';
import AuthShell from '../../components/Auth/AuthShell';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  // Dönüş adresi: yalnızca site içi göreli yol (dış URL'ye yönlendirme yok); kayıt/etkinleştirme adımlarına da taşınır
  const returnState = returnStateFrom(location);
  const redirectTo = returnState?.from || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('auth.login.errors.required'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await login(email, password);

      if (response && response.statusCode === 200) {
        // Timer'ı temizle
        localStorage.removeItem('activationTimer');
        localStorage.removeItem('activationTimerExpiry');

        const token = response.token || response.data?.token;
        if (!token) {
          console.error('Token not found in response');
          toast.error(t('auth.login.errors.general'));
          return;
        }

        // Token'ı şifrele ve sakla
        const encryptedToken = encrypt(token);
        if (!encryptedToken) {
          console.error('Token encryption failed');
          toast.error(t('auth.login.errors.general'));
          return;
        }

        localStorage.setItem('authToken', encryptedToken);

        // User datasını hazırla
        const userData = {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          isActivated: response.data.isActivated,
        };

        setUser(userData);

        if (!userData.isActivated) {
          rememberReturnTo(returnState?.from);
          navigate('/activation', { state: returnState });
        } else {
          rememberReturnTo(null);
          navigate(redirectTo, { replace: true });
        }

        toast.success(t('auth.login.success'));
      } else {
        toast.error(response?.message || t('auth.login.errors.invalidCredentials'));
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(t('auth.login.errors.general'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell>
      <Typography sx={{ fontSize: 13, fontWeight: 650, color: 'primary.main', letterSpacing: '0.02em' }}>
        {t('auth.login.welcome')}
      </Typography>
      <Typography component="h1" sx={{ mt: 0.75, fontSize: { xs: '1.6rem', sm: '1.85rem' }, fontWeight: 750, letterSpacing: '-0.025em', color: 'text.primary' }}>
        {t('auth.login.heading')}
      </Typography>
      <Typography sx={{ mt: 1, mb: 3.5, color: 'text.secondary', fontSize: '0.95rem', lineHeight: 1.55 }}>
        {t('auth.login.lead')}
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            required
            fullWidth
            id="email"
            label={t('auth.login.email.label')}
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.login.email.placeholder')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <TextField
              required
              fullWidth
              name="password"
              label={t('auth.login.password.label')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.login.password.placeholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? t('auth.login.password.hide') : t('auth.login.password.show')}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Link component={RouterLink} to="/forgot-password" underline="hover" sx={{ fontSize: 13.5, fontWeight: 600 }}>
                {t('auth.login.forgotPassword')}
              </Link>
            </Box>
          </Box>
        </Stack>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          endIcon={isLoading ? null : <ArrowForwardIcon />}
          sx={{ mt: 3, py: 1.4, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '0.98rem' }}
        >
          {isLoading ? <CircularProgress size={22} color="inherit" /> : t('auth.login.button')}
        </Button>
      </Box>

      <SocialAuthButtons mode="login" layout="row" returnTo={returnState?.from} />

      <Typography sx={{ mt: 3.5, textAlign: 'center', fontSize: 14, color: 'text.secondary' }}>
        {t('auth.login.noAccount')}{' '}
        <Link component={RouterLink} to="/register" state={returnState} underline="hover" sx={{ fontWeight: 700 }}>
          {t('auth.login.register')}
        </Link>
      </Typography>

      <Box
        sx={{
          mt: 3,
          pt: 2.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('auth.login.crmPrompt')}</Typography>
        <Link component={RouterLink} to="/crm/login" underline="hover" sx={{ fontSize: 13, fontWeight: 650 }}>
          {t('auth.login.crmLink')} →
        </Link>
      </Box>
    </AuthShell>
  );
};

export default LoginPage;
